/* -------------------------------------------------------------------------- */
/*                          Componente NumericInput                           */
/* -------------------------------------------------------------------------- */
// Este componente contiene un NumericInput genérico que se utiliza en muchas pantallas de la aplicación.
// Incluye fechas para aumentar la cantidad en incrementos de 1

import * as authSelectors from '@redux/reducers/auth';
import { FC, useEffect, useState } from 'react';
import { ControllerFieldState, ControllerRenderProps } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { FormGroup, Input, InputGroup, InputGroupAddon, InputGroupText, Label } from 'reactstrap';
import { testIsNumber } from 'utility/Utils';

interface Props {
  id: string | number | symbol;
  label?: string | JSX.Element;
  field: ControllerRenderProps | any;
  fieldstate: ControllerFieldState | any;
  required?: boolean;
  errorMessage?: string;
  placeholder?: string;
  autoCompleteInput?: any;
  prependcomponent?: string | any;
  appendcomponent?: string | any;
  metric?:
    | 'distance'
    | 'distance2'
    | 'fuel'
    | 'fuel2'
    | 'speed'
    | 'temperature'
    | 'capacity'
    | 'volume';
  min?: number;
  max?: number;
  step?: number;
  allowUndefined?: boolean;
  useNumberSpinner?: boolean;
  disabled?: boolean;
  allowDecimals?: boolean;
  decimalsPrecision?: number;
  additionalonchangefunction?: (value: number | undefined) => void;
  forceinitialvalue?: number;
  topRightComponent?: any;
  style?: any;
  allowZero?: boolean;
  forceShowDecimals?: boolean;
  onFocus?: (event) => void;
  readonly?: boolean;
  maxLength?: number;
  showCharCount?: boolean;
  /**
   * Se invoca cuando el usuario pega un texto que contiene un par de coordenadas
   * (ej. "14.6087, -90.4773"). Permite que el componente padre rellene el campo
   * actual y el de la otra coordenada. Si no se provee, al pegar un par se usa
   * únicamente el primer número para este campo.
   */
  onPasteCoordinatePair?: (first: number, second: number) => void;
}

const NumericalInput: FC<Props> = ({
  id,
  label,
  required,
  field,
  fieldstate,
  errorMessage,
  min = -10000000000,
  max = 100000000000,
  step = 1,
  useNumberSpinner = false,
  allowUndefined = true,
  metric,
  prependcomponent,
  appendcomponent,
  placeholder,
  allowDecimals = false,
  decimalsPrecision = 2,
  additionalonchangefunction,
  forceinitialvalue,
  autoCompleteInput,
  topRightComponent,
  disabled = false,
  style = {},
  allowZero,
  forceShowDecimals,
  onFocus,
  readonly = false,
  maxLength,
  showCharCount = maxLength != null,
  onPasteCoordinatePair
}) => {
  const [fieldValue, setInputValue] = useState(field.value);

  const { authSystems } = useSelector((state) => ({
    authSystems: authSelectors.getAuthSystems(state)
  }));

  const handleChangeNumber = (newValue) => {
    const validate = () => {
      const isEmptyString =
        typeof newValue === 'string' && // is a string
        newValue === '';
      const isNumber = !isNaN(Number(newValue));
      const hasNumberFormat = testIsNumber(newValue);

      return (
        isEmptyString || // regex that test number
        isNumber || // is not NaN
        hasNumberFormat // is a number
      );
    };

    if (validate()) {
      setInputValue(newValue);
    }
  };

  const onChangeFinal = (value) => {
    if (value) {
      let newValue: number | string = Math.max(Number(min), Math.min(Number(max), Number(value)));
      if (!allowDecimals) {
        newValue = Math.round(newValue);
      } else if (allowDecimals && forceShowDecimals) {
        newValue = Number(newValue).toFixed(decimalsPrecision);
      } else if (allowDecimals) {
        // Solo limitar decimales si es necesario, sin forzar el formato
        const valueAsNumber = Number(newValue);
        const fixedValue = Number(valueAsNumber.toFixed(decimalsPrecision));
        newValue = fixedValue;
      }
      handleChangeNumber(newValue);
      field.onChange(newValue);
      if (additionalonchangefunction) {
        additionalonchangefunction(Number(newValue));
      }
    } else if ((value === 0 || value === '0') && allowZero) {
      let newValue: string | number = Math.max(Number(min), Math.min(Number(max), Number(value)));
      if (!allowDecimals) {
        newValue = Math.round(newValue);
      } else if (allowDecimals && forceShowDecimals) {
        newValue = Number(newValue).toFixed(decimalsPrecision);
      } else if (allowDecimals) {
        // Solo limitar decimales si es necesario, sin forzar el formato
        const valueAsNumber = Number(newValue);
        const fixedValue = Number(valueAsNumber.toFixed(decimalsPrecision));
        newValue = fixedValue;
      }
      handleChangeNumber(newValue);
      field.onChange(newValue);
      if (additionalonchangefunction) {
        additionalonchangefunction(Number(newValue));
      }
    } else {
      field.onChange(undefined);
      if (additionalonchangefunction) {
        additionalonchangefunction(undefined);
      }
    }
  };

  const handleKeyDown = (event) => {
    if (allowDecimals) {
      if (event.key === '+' || event.key === 'e' || event.key === 'E') {
        event.preventDefault();
      }
    } else {
      if (event.key === '+' || event.key === 'e' || event.key === 'E' || event.key === '.') {
        event.preventDefault();
      }
    }

    if (event.key === '-' && min && min > 0) {
      event.preventDefault();
    }

    // avoid double dash
    if (event.target.value.length == 0 && event?.key === '-') {
      event.target.value = '';
    }
    if (event.target.value.length > 0 && event?.key === '-') {
      event.preventDefault();
    }
    // avoid double dash
  };

  const handlePaste = (event) => {
    const pasted =
      event.clipboardData?.getData('text') ??
      event.clipboardData?.getData('Text') ??
      (window as any).clipboardData?.getData('Text') ??
      '';
    // Extrae todos los números (con signo y decimales) presentes en el texto pegado.
    const numbers = pasted.match(/-?\d+(?:\.\d+)?/g);
    if (!numbers || numbers.length === 0) {
      // En <input type="number"> el navegador puede descartar un pegado inválido
      // (ej. "lat, long") y dejar el campo vacío. Prevenimos ese comportamiento.
      event.preventDefault();
      return;
    }

    event.preventDefault();

    const first = Number(numbers[0]);
    // Caso par de coordenadas: si hay 2+ números y el padre sabe distribuirlos,
    // delega para que rellene este campo y el de la otra coordenada.
    if (numbers.length >= 2 && onPasteCoordinatePair) {
      onPasteCoordinatePair(first, Number(numbers[1]));
      return;
    }

    // Caso un solo número (o sin callback): se aplica al campo actual.
    handleChangeNumber(first);
    onChangeFinal(first);
  };

  useEffect(() => {
    handleChangeNumber(field.value);
    onChangeFinal(field.value);
  }, [field.value]);

  useEffect(() => {
    if (forceinitialvalue) handleChangeNumber(forceinitialvalue);
    if (forceinitialvalue) onChangeFinal(forceinitialvalue);
  }, [forceinitialvalue]);
  return (
    <FormGroup>
      <Label className="form-label" for={id} style={style}>
        {label}
        {required && <span className="required">&nbsp;*</span>}
        {topRightComponent != null && topRightComponent}
      </Label>

      <InputGroup className={useNumberSpinner ? '' : 'number-field-hide-spin'}>
        {prependcomponent && (
          <InputGroupAddon addonType="prepend">{prependcomponent}</InputGroupAddon>
        )}

        <Input
          id={String(id) + 'text'}
          disabled={disabled}
          onBlur={(event) => {
            onChangeFinal(fieldValue);
          }}
          min={min}
          max={max}
          maxLength={maxLength}
          invalid={fieldstate.error != null || (maxLength != null && String(fieldValue ?? '').length > maxLength)}
          type="number"
          step={allowDecimals ? 'any' : step}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onChange={(event) => {
            const v = event.target.value;
            if (maxLength != null && v.length > maxLength) return;
            handleChangeNumber(v);
          }}
          value={fieldValue ?? ''}
          placeholder={placeholder}
          autoComplete={autoCompleteInput}
          onFocus={onFocus}
        />
        {(appendcomponent || metric) && (
          <InputGroupAddon addonType="append">
            {metric ? authSystems[metric] : <InputGroupText>{appendcomponent}</InputGroupText>}
          </InputGroupAddon>
        )}
      </InputGroup>
      {fieldstate.error ? (
        <div className="invalid-feedback" style={{ display: 'block' }}>
          {fieldstate.error?.message || errorMessage || (
            <FormattedMessage id="common.requiredField" />
          )}
        </div>
      ) : null}
      {showCharCount && maxLength != null && String(fieldValue ?? '').length >= maxLength && (
        <small className="form-text text-danger">
          <FormattedMessage
            id="common.characterCount"
            values={{ count: String(fieldValue ?? '').length, max: maxLength }}
          />
        </small>
      )}
    </FormGroup>
  );
};

export default NumericalInput;
